<template>
  <div
    class="w-full h-full flex items-center justify-center cursor-pointer"
    @click="handleTogglePlayback"
  >
    <template v-if="isVideo">
      <video
        v-show="isLoaded"
        ref="playerRef"
        controls
        :poster="poster"
        class="h-[inherit]"
      />
      <template v-if="!isLoaded">
        <img
          v-if="poster && !posterErrored"
          :src="poster"
          alt=""
          class="size-full object-contain"
          @error="posterErrored = true"
        />
        <div
          v-else
          class="size-[170px] tablet-v:size-[280px] tablet-h:size-[320px]"
        >
          <Thumbnail type="video" />
        </div>
      </template>
    </template>
    <template v-if="isAudio">
      <div class="size-[170px] tablet-v:size-[280px] tablet-h:size-[320px]">
        <Thumbnail type="audio" />
      </div>
      <audio ref="playerRef" controls class="w-full absolute bottom-0" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { FileType } from '@/shared/graphql/legacy/generated'
import { Thumbnail } from '@dacr/library'
import { getTokens } from '@dacr/auth'

const props = defineProps<{
  type: FileType
  url?: string | null
  poster?: string
}>()

const playerRef = ref<HTMLVideoElement | HTMLAudioElement | null>(null)
const isLoaded = ref(false)
const isPlaying = ref(false)
const posterErrored = ref(false)

watch(
  () => props.poster,
  () => (posterErrored.value = false)
)

let shakaPlayer: { destroy: () => Promise<unknown> } | null = null

const isAudio = computed(() => props.type === FileType.Audio)
const isVideo = computed(() => props.type === FileType.Video)

const handleTogglePlayback = () => {
  if (!playerRef.value) return

  isPlaying.value = !isPlaying.value

  if (isPlaying.value) {
    playerRef.value.play()
  } else {
    playerRef.value.pause()
  }
}

onMounted(async () => {
  if (!playerRef.value) return

  const shaka = await import('shaka-player')

  shaka.default.polyfill.installAll()

  if (!playerRef.value) return

  if (shaka.default.Player.isBrowserSupported()) {
    const player = new shaka.default.Player()
    shakaPlayer = player

    await player.attach(playerRef.value)

    player.addEventListener('loaded', () => {
      isLoaded.value = true
    })

    player
      .getNetworkingEngine()
      ?.registerRequestFilter((_: unknown, request: { uris: string[] }) => {
        const url = new URL(request.uris[0])
        url.searchParams.set(
          'Authorization',
          `Bearer ${getTokens().accessToken}`
        )
        request.uris[0] = url.toString()
      })

    try {
      await player.load(props.url)
    } catch {
      isLoaded.value = false
    }
  }
})

onUnmounted(() => {
  shakaPlayer?.destroy()
  shakaPlayer = null
})
</script>
